/** @jsxImportSource @emotion/react */
import * as R from "ramda";
import { getResource } from "../language/resources/getResource";
import { Column } from "../layout/flexbox";
import infoImg from "../assets/icons/info.svg";
import { TooltipFC } from "../layout/TooltipFC";
import { MediumText, PAGE_COLUMN, PAGE_TITLE, XLargeText } from "../styles/styles";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import {
  BLUE_CREEK,
  DARK_BLUE_CREEK,
  LIGHT_GREY,
  RED_CREEK,
  WHITE,
} from "../styles/colors";
import { FADE_IN_KEYFRAMES } from "../styles/animations";
import importGuestIcon from "../assets/icons/save.svg";
import hotlistIcon from "../assets/icons/flame.svg";
import visitorIcon from "../assets/icons/visitor.svg"
import { showModalAction } from "../core/modal/showModalAction";
import { IKON_MODAL } from "../core/modal/ikonModal";
import { useCallback, useEffect } from "react";
import { getGuestHydrationErrorsCountAction } from "./hydrationErrorReport/getGuestHydrationErrorsCountAction";
import { showSnackbarAction } from "../snackbar/snackbarAction";
import { ApiError } from "../core/redux/api/apiError";
import { clearApiErrorAction } from "../core/redux/api/clearApiErrorAction";
import { ACTION_TYPE } from "../core/redux/actionType";
import { getError } from "../core/redux/api/module";
import { css } from "@emotion/react";

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -11px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
`;

const titleRow = css`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const reportButton = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 350px;
  text-align: center;
  text-decoration: none;
  ${XLargeText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${WHITE};
  border: solid 2px ${BLUE_CREEK};
  padding: 15px 15px;
  color: ${BLUE_CREEK};

  &:hover {
    background-color: ${LIGHT_GREY};
    color: ${DARK_BLUE_CREEK};
  }

  &:disabled,
  &:disabled:hover {
    background-color: ${WHITE}; /* Keep original color */
    color: ${BLUE_CREEK};
    cursor: not-allowed;
    opacity: 0.6; /* Visually indicate disabled */
  }
`;

const buttonImage = css`
  align-items: right;
  width: 40px;
`;

const visitorButtonImage = css`
  align-items: right;
  width: 35px;
`;


const buttonList = css`
  margin-top: 75px;
  display: flex;
  gap: 30px;
`;

const guestHydrationErrorButtonContainer = css`
  display: grid;
  grid-template-columns: auto 50px;
  width: fit-content;
`;

const errorIndicatorContainer = css`
  display: grid;
  position: relative;
  right: 75%;
  top: -35px;
  width: fit-content;
  scale: 0.75;
  z-index: 1;
`;

const errorIndicator = css `
  margin: auto;
    padding: 5px;
    width: 40px;
    height: 25px;
    border-radius: 25px;
    text-align: center;
    vertical-align: middle;
    color: ${WHITE};
    background-color: ${RED_CREEK};
     ${MediumText};
`;

const valueTextLabel = css`
  display:inline-block;
  vertical-align: middle;
  padding-top:3px;
`;

export const ReportsPage = () => {
  const dispatch = useDispatch();
  const {
    resources,
    selectedResort,
    guestHydrationLoadingError,
    guestHydrationErrorCount,
    enableCustomerImport,
    showHotlistReport,
    showGuestHydrationReport,
    showWeeklyVisitorReport,
  } = useSelector((state: IAppState) => ({
    resources: state.locale,
    selectedResort: state.selectedResort,
    guestHydrationErrorReport: state.guestHydrationErrorReport,
    guestHydrationErrorCount: state.guestHydrationErrorReportCount,
    // Resort-keyed reducer: request id is the resort abbreviation, not the action type
    guestHydrationLoadingError: getError(
      state,
      state.selectedResort.resortAbbreviation,
      ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_COUNT_REQUEST
    ),
    enableCustomerImport: R.includes(
      state.userState.role,
      state.resortToolsConfig.enableCustomerImport
    ),
    showHotlistReport: state.resortToolsConfig.enableHotlistReport.includes(state.userState.role ? state.userState.role : ""),
    showGuestHydrationReport: state.resortToolsConfig.enableGuestHydrationReport.includes(state.userState.role ? state.userState.role : ""),
    showWeeklyVisitorReport: state.resortToolsConfig.weeklyVisitorReportLimit > 0 &&
                             state.resortToolsConfig.enableWeeklyVisitorReport.includes(state.userState.role ? state.userState.role : ""),
  }));

  const onTooltipClick = (event) => event.preventDefault();

  const onHydrationReportClick = () =>
    dispatch(showModalAction(IKON_MODAL.GUEST_HYDRATION_REPORT, {}));

  const onHotlistReportClick = () => 
    dispatch(showModalAction(IKON_MODAL.HOTLIST_REPORT, {}));

  const onWeeklyVisitorDataReportClick = () => {
    dispatch(showModalAction(IKON_MODAL.WEEKLY_VISITOR_REPORTS, {}));
  }
  
  const clearError = useCallback((error: ApiError) =>
    dispatch(clearApiErrorAction(error.requestId, error.actionType)), [dispatch]);
  
  const showError = useCallback((errorMsg: string) =>
    dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);

  useEffect(() => {
    if (enableCustomerImport) {
      dispatch(
        getGuestHydrationErrorsCountAction(selectedResort.resortAbbreviation)
      );
    }
  }, [selectedResort, enableCustomerImport, dispatch]);

  useEffect(() => {
    if (guestHydrationLoadingError) {
      clearError(guestHydrationLoadingError);
      showError(
        getResource(resources, "GET_GUEST_HYDRATION_ERRORS_COUNT_ACTION_ERROR")
      );
    }
  }, [clearError, guestHydrationLoadingError, resources, showError]);


  return (
    <Column rootCss={PAGE_COLUMN}>
      <div css={titleRow}>
        <span css={PAGE_TITLE}>
          {getResource(resources, "REPORTS_TITLE")}
        </span>
        <TooltipFC
          content={<span>{getResource(resources, "REPORTS_PAGE_INFO")}</span>}
          children={
            <img
              alt="ⓘ"
              src={infoImg}
              css={infoStyle}
              onClick={onTooltipClick}
            />
          }
        />
      </div>
      <Column rootCss={buttonList}>
      {showHotlistReport &&
        <button
          css={reportButton}
          onClick={onHotlistReportClick}
        >
          <span>
            {getResource(resources, "REPORTS_HOTLIST_REPORT")}
          </span>
          <img css={buttonImage} src={hotlistIcon} alt="" />
        </button>
      }
      {enableCustomerImport && showGuestHydrationReport &&
        <span css={guestHydrationErrorButtonContainer}>
          <button
            css={reportButton}
            onClick={onHydrationReportClick}
            disabled={guestHydrationErrorCount === 0}
            title={
              guestHydrationErrorCount === 0
                ? getResource(
                    resources,
                    "REPORTS_GUEST_HYDRATION_ERRORS_NO_ERRORS_TOOLTIP"
                  )
                : undefined
            }
          >
            <span>
              {getResource(resources, "REPORTS_GUEST_HYDRATION_ERRORS")}
            </span>
            <img css={buttonImage} src={importGuestIcon} alt="" />
          </button>
          {guestHydrationErrorCount !== undefined && guestHydrationErrorCount > 0 && (
            <span css={errorIndicatorContainer}>
              <div css={errorIndicator}>
                <div css={valueTextLabel}>!!</div>
              </div>
            </span>
          )}
        </span>
        }
        {showWeeklyVisitorReport && (
          <button
            css={reportButton}
            onClick={onWeeklyVisitorDataReportClick}
          >
            <span>
              {getResource(resources, "REPORTS_WEEKLY_VISTOR_DATA")}
            </span>
            <img css={visitorButtonImage} src={visitorIcon} alt="" />
          </button>
        )}
      </Column>
    </Column>
  );
};
